<template>
  <div
    :class="containerClasses"
    data-ui="price-register"
  >
    <div
      v-if="showPricing"
      class="price-register__pricing"
      data-ui="session-register-price"
    >
      <CdrText
        v-if="computedMemberPrice"
        class="price-register__member-price"
      >
        <span
          class="price-register__member-price-highlight"
          data-ui="session-register-member-price"
        >
          {{ computedMemberPrice }}
        </span>
        <span>&nbsp;{{ computedMemberPriceLabel }}</span>
      </CdrText>
      <CdrText
        v-if="computedNonMemberPrice"
        class="price-register__nonmember-price"
      >
        <span data-ui="session-register-nonmember-price">
          {{ computedNonMemberPrice }}
        </span>
        <span>&nbsp;{{ computedNonMemberPriceLabel }}</span>
      </CdrText>
    </div>

    <template v-if="isMoved">
      <CdrText
        class="price-register__session-moved-message"
        data-ui="session-moved-error-msg"
      >
        Hmm. Something went wrong. Please go back and try to find this course again later.
      </CdrText>
    </template>

    <template v-else-if="showThirdParty">
      <CdrButton
        :full-width="true"
        class="price-register__cta"
        data-ui="session-register-third-party"
        size="large"
        tag="a"
        :href="registerLink.uri"
      >
        <template #icon-right>
          <IconExternalLink inherit-color />
        </template>
        Register
      </CdrButton>
    </template>

    <template v-if="showJoinWaitlist">
      <CdrButton
        v-if="fromTrek && dayWaitlistEnabled"
        :full-width="true"
        class="session-price__waitlist"
        data-ui="session-price-waitlist"
        modifier="secondary"
        size="large"
        tag="a"
        :href="`${dayWaitlistUri}?departure=${sessionId}`"
      >
        {{ ctaText }}
      </CdrButton>
      <CdrText
        v-else-if="fromTrek"
        data-ui="session-price-notify-me-error-msg"
      >
        Call
        <CdrLink
          :href="computedOperatorPhoneNumber.href"
          @click="metricsClick('{{ pageName }}_trek call to join wait list')"
        >
          {{ computedOperatorPhoneNumber.display }}
        </CdrLink>
        or email
        <CdrLink
          :href="computedOperatorEmail.href"
          @click="metricsClick('{{ pageName }}_trek email to join wait list')"
        >
          {{ computedOperatorEmail.display }}
        </CdrLink>
        with your name, email, and location of the event to join the waitlist
      </CdrText>
      <NotifyMeComponent
        v-else
        :modal-data="modalData"
        data-ui="session-price-notify-me"
      />
      <CdrLink
        v-if="otherSessions"
        :href="moreDatesUrl"
        class="session-price__more-dates"
        modifier="standalone"
        data-ui="more-dates-link"
        @click="handleClick"
      >
        View more dates
      </CdrLink>
    </template>

    <!-- registration was at one point possible but is not anymore. show disabled register button -->
    <template v-else-if="isRegistrationDisabled && !isMoved">
      <CdrButton
        v-if="isClosed || isCancelled || isSoldOut"
        :full-width="true"
        class="price-register__cta"
        data-ui="session-register-disabled-button"
        disabled
        size="large"
      >
        {{ ctaText }}
      </CdrButton>

      <!-- third-party -->
      <CdrText
        v-else
        class="price-register__cta price-register__cta--disabled"
        data-ui="session-register-disabled-button"
        tag="p"
      >
        {{ ctaText }}
      </CdrText>
    </template>

    <!-- unless registration is handled by 3rd party, show register button to all cases -->
    <form
      v-else-if="!isThirdParty && !isMoved"
      :action="registrationUrl"
      class="price-register__form"
      data-ui="session-register-submit-button"
      method="post"
    >
      <CdrButton
        v-if="fromTrek"
        :full-width="true"
        class="price-register__cta"
        data-ui="register-button"
        size="large"
        type="submit"
        @click="handleTrekClick"
      >
        {{ ctaText }}
      </CdrButton>
      <CdrButton
        v-else
        :full-width="true"
        class="price-register__cta"
        data-ui="register-button"
        size="large"
        type="submit"
        @click="handleClick"
      >
        {{ ctaText }}
      </CdrButton>
    </form>

    <span
      v-if="showRegistrationStatusMessage"
      class="price-register__available-spots"
      data-ui="session-register-available-spots"
    >
      {{ registrationStatusMessage }}
    </span>
    <CdrLink
      v-else-if="otherSessions && !showJoinWaitlist"
      :href="moreDatesUrl"
      :class="viewMoreSessionsClasses"
      modifier="standalone"
      data-ui="more-dates-link"
      @click="handleClick"
    >
      View more dates
    </CdrLink>
  </div>
</template>
<script>
import moment from 'moment';
import { CdrButton, CdrLink, CdrText, IconExternalLink } from '@rei/cedar';
import NotifyMeComponent from '../legacy/components/notify-me/index.vue';
import metrics from '../mixins/metrics';
import constants from '../common/constants';

export default {
  name: 'SessionPriceRegister',
  components: {
    CdrButton,
    CdrLink,
    CdrText,
    NotifyMeComponent,
    IconExternalLink,
  },
  mixins: [metrics],
  props: {
    deviceSize: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    },
    gpsLat: {
      type: Number,
      required: true,
    },
    gpsLong: {
      type: Number,
      required: true,
    },
    loc: {
      type: String,
      required: true,
    },
    memberPrice: {
      type: Number,
      required: true,
    },
    moreDatesUrl: {
      type: String,
      required: true,
    },
    nonMemberPrice: {
      type: Number,
      required: true,
    },
    otherSessions: {
      type: Boolean,
      required: true,
    },
    pageTitle: {
      type: String,
      required: true,
    },
    partners: {
      type: Array,
      required: true,
    },
    registrationRequired: {
      type: Boolean,
      required: true,
    },
    sessionId: {
      type: [Number, String],
      required: true,
    },
    spots: {
      type: Object,
      required: true,
    },
    startDate: {
      type: String,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },
    fromTrek: {
      type: Boolean,
      default: false,
    },
    operatorEmail: {
      type: String,
      default: '',
    },
    operatorPhoneNumber: {
      type: String,
      default: '',
    },
    dayWaitlistEnabled: {
      type: Boolean,
      required: true,
    },
    dayWaitlistUri: {
      type: String,
      required: true,
    },
  },
  computed: {
    containerClasses() {
      return {
        'price-register': true,
        'price-register--canceled': this.isCancelled,
        'price-register--closed': this.isClosed,
        'price-register--open': this.isOpen,
        'price-register--limited': this.isLimitedSpotsLeft,
        'price-register--reg-disabled': this.isRegistrationDisabled,
        'price-register--reg-required': this.isRegRequired,
        'price-register--third-party': this.isThirdParty,
        'price-register--waitlisted': this.isWaitListed,
        'price-register--sold-out': this.isSoldOut,
      };
    },
    computedMemberPrice() {
      if (this.memberPrice < 0) {
        return '';
      }
      return this.memberPrice > 0 ? `$${this.memberPrice}` : 'Free';
    },
    computedNonMemberPrice() {
      if (this.nonMemberPrice < 0) {
        return '';
      }
      return this.nonMemberPrice > 0 ? `$${this.nonMemberPrice}` : 'Free';
    },
    computedMemberPriceLabel() {
      return this.memberPrice < 0 ? '' : 'for REI Members';
    },
    computedNonMemberPriceLabel() {
      return this.nonMemberPrice < 0 ? '' : 'for non-members';
    },
    ctaText() {
      switch (true) {
        case this.isClosed:
          return 'Closed';
        case this.isCancelled:
          return 'Cancelled';
        case !this.isRegRequired:
          return 'Registration not required';
        case this.isWaitListed:
          return 'Join the waitlist';
        case this.isSoldOut:
          return 'Sold out';
        case this.isThirdParty:
          return 'Register on event website';
        default:
          return 'Register';
      }
    },
    isCancelled() {
      return this.status === 'CANCELED';
    },
    isClosed() {
      return this.status === 'CLOSED';
    },
    isLimitedSpotsLeft() {
      const { spots: { available } = {} } = this;
      return available > 0 && available <= 5;
    },
    isOpen() {
      return this.status === 'OPEN';
    },
    isRegistrationDisabled() {
      return !this.isOpen || !this.isRegRequired || this.isSoldOut;
    },
    isRegRequired() {
      return this.registrationRequired === true;
    },
    isThirdParty() {
      return this.spots.registrationLocation === 'THIRD_PARTY';
    },
    isWaitListed() {
      return this.spots.status === 'WAIT_LIST';
    },
    isSoldOut() {
      return this.spots.status === 'SOLD_OUT';
    },
    registrationUrl() {
      return '';
    },
    viewMoreSessionsClasses() {
      const showSeparator = !this.isRegRequired || this.showThirdParty;
      return [
        'session-price__more-dates',
        { 'session-price__more-dates--with-separator': showSeparator },
      ];
    },
    showThirdParty() {
      let isRegisterHere = false;
      this.partners.forEach((partner) => {
        if (partner.name === 'REGISTER HERE') isRegisterHere = true;
      });
      return isRegisterHere && this.isThirdParty && !this.isClosed && !this.isCancelled;
    },
    registerLink() {
      return this.partners.find((partner) => partner.name === 'REGISTER HERE');
    },
    showRegistrationStatusMessage() {
      return (
        !this.isSoldOut &&
        this.isRegRequired &&
        !this.isCancelled &&
        this.registrationStatusMessage !== '' &&
        !this.isMoved
      );
    },
    showJoinWaitlist() {
      return (
        this.isWaitListed &&
        this.isRegRequired &&
        !this.isSoldOut &&
        !this.isCancelled &&
        !this.isClosed &&
        !this.isThirdParty
      );
    },
    showPricing() {
      return (
        this.isRegRequired &&
        !this.isThirdParty &&
        (this.computedMemberPrice || this.computedNonMemberPrice)
      );
    },
    registrationStatusMessage() {
      const {
        isWaitListed,
        isThirdParty,
        isCancelled,
        isClosed,
        isSoldOut,
        isLimitedSpotsLeft,
        isRegRequired,
        spots: { available } = {},
      } = this;
      let message = '';

      // Conditions that meet the eligibility that registration is available
      if (
        isRegRequired &&
        !isSoldOut &&
        !isClosed &&
        !isCancelled &&
        !isThirdParty &&
        !isLimitedSpotsLeft &&
        !isWaitListed
      ) {
        if (available === null) {
          message = 'Come one, come all!';
        } else {
          message = `${available} spots left`;
        }
      } else if (isLimitedSpotsLeft && !isWaitListed) {
        message = 'Limited spots left';
      } else if (isClosed || isSoldOut) {
        message = 'Registration closed';
      }
      return message;
    },
    modalData() {
      // eslint-disable-next-line
      const { gpsLat, gpsLong, loc, moreDatesUrl, pageTitle: title, sessionId } = this;
      const tzStart = moment.parseZone(this.startDate);
      const tzEnd = moment.parseZone(this.endDate);
      const startDate = moment(tzStart).format(constants.DATE_DISPLAY.LONG);
      const endDate = moment(tzEnd).format(constants.DATE_DISPLAY.LONG);
      const startTime = moment(tzStart).format(constants.DATE_DISPLAY.TIME);
      const endTime = moment(tzEnd).format(constants.DATE_DISPLAY.TIME);
      return {
        endDate,
        endTime,
        sessionId,
        lat: gpsLat,
        loc,
        long: gpsLong,
        startTime,
        startDate,
        title,
        uri: moreDatesUrl,
      };
    },
    /**
     * Determines whether the class has a status of MOVED
     * @returns a bool
     */
    isMoved() {
      return this.status.toLowerCase() === 'moved';
    },
    computedOperatorEmail() {
      return {
        href: `mailto:${this.operatorEmail}`,
        display: this.operatorEmail,
      };
    },
    computedOperatorPhoneNumber() {
      const formattedPhoneNumber = this.operatorPhoneNumber.replace('(', '').replace(')', '-');
      const splitPhoneNumber = formattedPhoneNumber.split('-');
      return {
        href: `tel:+1-${formattedPhoneNumber}`,
        display: `1 (${splitPhoneNumber[0]}) ${splitPhoneNumber[1]}-${splitPhoneNumber[2]}`,
      };
    },
  },
  methods: {
    handleClick({ target }) {
      const { innerText } = target;
      this.isModalOpen = true;
      const linkName = `{{ pageName }}_register bar_${innerText}`;
      this.metricsClick({ linkName });
    },
    handleTrekClick(e) {
      e.preventDefault();
      const { innerText } = e.target;
      const linkName = `{{ pageName }}_register trek_${innerText}`;
      this.metricsClick({ linkName });
      window.location = `/events/AddSessionToAdventuresCart?sessionId=${this.sessionId}`;
    },
  },
};
</script>
